// /admin/tracker?scope=all|<projectId>&from&to (architecture sprint-05 §6.3, S9.2; sprint-11 §9.3). Default:
// this week up to today in the Admin's zone; each row's future cut-off is that user's own today (server
// side). Scope "all" is the sprint-5 view, unchanged; a project scope shows that project's members, its
// working days and hours on it only.
import { formatWorkweek } from '@shared/workweek';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { ALL_PROJECTS_SCOPE, ProjectScopeSelect, resolveScope } from '@/components/ProjectScopeSelect';
import { RangePicker, type RangePreset } from '@/components/RangePicker';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useOversightProjects, useScopedTracker } from '@/hooks/use-oversight';
import { isValidReportRange, type DateRange } from '@/lib/report-model';
import { defaultTrackerRange, recordsLinkFor, TRACKER_PRESET_LABELS, trackerPresetRange } from '@/lib/tracker-model';
import { useToday } from '@/lib/use-now';
import { TrackerGrid } from './TrackerGrid';

export function TrackerPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="6xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  return <TrackerView tz={me.timezone} />;
}

function TrackerView({ tz }: { tz: string }): JSX.Element {
  const today = useToday(tz);
  const [params, setParams] = useSearchParams();
  const all = useOversightProjects();
  const projects = { data: all.data?.filter((p) => p.shiftEnabled !== true) };
  const rawFrom = params.get('from');
  const rawTo = params.get('to');
  const rawScope = params.get('scope');
  const valid = isValidReportRange(rawFrom, rawTo);
  const range: DateRange = valid && rawFrom && rawTo ? { from: rawFrom, to: rawTo } : defaultTrackerRange(today);
  // Until the project list loads, any scope is taken as given; then an unknown one falls back to All.
  const resolved = projects.data ? resolveScope(rawScope, projects.data, true) : { scope: rawScope ?? ALL_PROJECTS_SCOPE, valid: true };
  const scope = resolved.scope ?? ALL_PROJECTS_SCOPE;
  const project = scope === ALL_PROJECTS_SCOPE ? null : (projects.data?.find((p) => p.id === scope) ?? null);

  useEffect(() => {
    if (!valid || !resolved.valid || rawScope === null) setParams({ scope, from: range.from, to: range.to }, { replace: true });
  }, [valid, resolved.valid, rawScope, scope, range.from, range.to, setParams]);

  const tracker = useScopedTracker(scope === ALL_PROJECTS_SCOPE ? null : scope, range.from, range.to);
  const presets: RangePreset[] = TRACKER_PRESET_LABELS.map((p) => ({ key: p.key, label: p.label, range: trackerPresetRange(p.key, today) }));

  return (
    <Page width="6xl">
      <PageHeader title="Missing entries" hint="Working days with no time logged are highlighted. Select a day to open it in Records." />
      <div className="mb-4">
        <ProjectScopeSelect id="tracker-scope" value={scope} projects={projects.data ?? []} allowAll onChange={(s) => setParams({ scope: s, from: range.from, to: range.to })} />
      </div>
      <RangePicker from={range.from} to={range.to} presets={presets} onChange={(r) => setParams({ scope, from: r.from, to: r.to })} />
      {tracker.data && (
        <div className="mb-3 space-y-1 text-sm">
          {project ? (
            <p>
              Members of {project.name} · {formatWorkweek(tracker.data.workweekDays)}
            </p>
          ) : (
            <p>
              Working days: {formatWorkweek(tracker.data.workweekDays)}{' '}
              <Link to="/admin/settings" className="text-link underline-offset-4 hover:underline">
                Change
              </Link>
            </p>
          )}
          <p className="text-muted-foreground">Days off and holidays aren&apos;t tracked; they show as missing.</p>
        </div>
      )}
      {!tracker.data ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : tracker.data.dates.length === 0 ? (
        <EmptyState title="No working days in this range." />
      ) : (
        <TrackerGrid tracker={tracker.data} linkFor={(userId, date) => recordsLinkFor(userId, date, project?.id)} />
      )}
    </Page>
  );
}
